type Provider<T> = () => T
type Predicate<T> = (t: T) => boolean
type Consumer<T> = (t: T) => void
type Mapper<T, U> = (t: T) => U
type OptionMapper<T, U> = Mapper<T, Option<U>>

abstract class Option<T> {
  static some<T>(t: T): Option<T> {
    return new Some(t)
  }

  static none<T>(): Option<T> {
    return NoneInstance
  }

  static option<T>(t: T | undefined | null): Option<T> {
    return t ? Option.some(t) : Option.none()
  }

  static merge<T, U>(first: Option<T>, second: Option<U>): Option<[T, U]> {
    const error = new Error('Error while merging options');

    if (first.isEmpty() || second.isEmpty()) {
      return Option.none()
    }

    const t: [T, U] = [first.getOrFail(error), second.getOrFail(error)];
    return Option.some(t)
  }

  abstract isEmpty(): boolean

  abstract isNotEmpty(): boolean

  abstract getOrElse(provider: Provider<T> | T): T

  abstract orElse(provider: Provider<T> | T): Option<T>

  abstract getOrFail(error?: Provider<Error> | Error): T

  abstract getOrNull(): T | null

  abstract getOrUndefined(): T | undefined

  abstract map<U>(mapper: Mapper<T, U>): Option<U>

  abstract flatMap<U>(mapper: OptionMapper<T, U>): Option<U>

  abstract filter(predicate: Predicate<T>): Option<T>

  abstract exists(predicate: Predicate<T>): boolean

  abstract forEach(consumer: Consumer<T>): void
}

class Some<T> extends Option<T> {
  t: T;

  constructor(t: T) {
    super();
    this.t = t
  }

  isEmpty(): boolean {
    return false
  }

  isNotEmpty(): boolean {
    return true
  }

  getOrElse(provider: Provider<T> | T): T {
    return this.t
  }

  orElse(provider: Provider<T> | T): Option<T> {
    return this
  }

  getOrFail(error?: Provider<Error> | Error): T {
    return this.t
  }

  getOrNull(): T | null {
    return this.t
  }

  getOrUndefined(): T | undefined {
    return this.t
  }

  map<U>(mapper: Mapper<T, U>): Option<U> {
    return new Some(mapper(this.t))
  }

  flatMap<U>(mapper: OptionMapper<T, U>): Option<U> {
    return mapper(this.t)
  }

  filter(predicate: Predicate<T>): Option<T> {
    const result = predicate(this.t);
    // eslint-disable-next-line
    return result ? this : NoneInstance
  }

  exists(predicate: Predicate<T>): boolean {
    return predicate(this.t)
  }

  forEach(consumer: Consumer<T>): void {
    consumer(this.t)
  }
}

class None<T> extends Option<T> {
  isEmpty(): boolean {
    return true
  }

  isNotEmpty(): boolean {
    return false
  }

  getOrElse(provider: Provider<T> | T): T {
    return typeof provider === 'function' ? provider() : provider
  }

  orElse(provider: Provider<T> | T): Option<T> {
    return new Some(typeof provider === 'function' ? provider() : provider)
  }

  getOrFail(error?: Provider<Error> | Error): T {
    if (error) {
      throw typeof error === 'function' ? error() : error
    }

    throw new Error('Option is empty')
  }

  getOrNull(): T | null {
    return null
  }

  getOrUndefined(): T | undefined {
    return undefined
  }

  map<U>(mapper: Mapper<T, U>): Option<U> {
    return NoneInstance
  }

  flatMap<U>(mapper: OptionMapper<T, U>): Option<U> {
    return NoneInstance
  }

  filter(predicate: Predicate<T>): Option<T> {
    return NoneInstance
  }

  exists(predicate: Predicate<T>): boolean {
    return false
  }

  // tslint:disable-next-line:no-empty
  forEach(consumer: Consumer<T>): void {}
}

// tslint:disable-next-line:no-any
const NoneInstance: None<any> = new None();

export const option = <T>(t: T | undefined | null): Option<T> => Option.option(t);

export const some = <T>(t: T): Option<T> => Option.some(t);

export const none = <T>(): Option<T> => Option.none();

export default Option
